/**
 * Nudge (kanban 68cc2cfb): Jess re-messages the person an item waits on.
 *
 * Scott to Jess, 2026-10-06: "the ability for you to nudge somebody and
 * it'll send them a new message, and it'll be like Jess has requested
 * that, we not you". The pure parts live here: who a Routed-away list is
 * with and how many items each holds, when Jess first asked a person about
 * an item (what the nudge will say), why a routed-away item cannot be
 * nudged yet (the hand-off Merlin has not released, or that never reached
 * them: the backend refuses those too), the words of the confirm step and
 * the "nudged <date>" line.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { shortDate } from '../../components/portal/threadTriageView';
import { routeeNames, sameName } from './deliveryView';
import { namesInclude, peopleWaitedOn, type PersonCount } from './listFilter';
import { openAskWait } from './askWaitView';
import { waitingOn } from './waitingOnView';

/** One nudge: the item, the person as the item names them, and when Jess
 *  first asked them (what the message will say); null when unknown. */
export interface NudgeTarget {
  thread: DoorThreadRead;
  who: string;
  since: string | null;
}

/* ---------------------------------------------------------------- */
/* Who an item is with (Routed away)                                 */
/* ---------------------------------------------------------------- */

/** Who the routed-away items are with, and how many each holds, most
 *  first: "Laura Caswell · 3", "Cliff Platt · 1". An item with two people
 *  counts once for each. */
export function routedAwayPeople(threads: Pick<DoorThreadRead, 'routed_to'>[]): PersonCount[] {
  return peopleWaitedOn(threads.map((t) => routeeNames(t.routed_to)));
}

/** The routed-away items with this person (loose name match, the same
 *  rule the Triage person filter uses). */
export function routedAwayWith<T extends Pick<DoorThreadRead, 'routed_to'>>(threads: T[], who: string): T[] {
  return threads.filter((t) => namesInclude(routeeNames(t.routed_to), who));
}

/* ---------------------------------------------------------------- */
/* What the nudge will say                                           */
/* ---------------------------------------------------------------- */

/**
 * When Jess first asked `who` about this item, in the order the backend
 * reads it: their oldest open ask on it, else the wait record on them,
 * else when it was sent to them. Null when nothing dates it.
 */
export function nudgeSince(
  thread: DoorThreadRead,
  requests: DoorDataRequestRead[],
  who: string,
): string | null {
  const asked = openAskWait(thread, requests);
  if (asked && asked.who.some((n) => sameName(n, who))) return asked.since;
  const w = waitingOn(thread);
  if (w && sameName(w.who, who)) return w.since;
  if (routeeNames(thread.routed_to).some((n) => sameName(n, who))) return thread.routed_at;
  return null;
}

/** The people one of Jess's waiting items can be nudged about: everyone it
 *  waits on, each with when she first asked them. */
export function waitingTargets(
  thread: DoorThreadRead,
  requests: DoorDataRequestRead[],
  who: string[],
): NudgeTarget[] {
  return who.map((name) => ({ thread, who: name, since: nudgeSince(thread, requests, name) }));
}

/** The people a routed-away item is with, each a nudge target. */
export function routedTargets(thread: DoorThreadRead): NudgeTarget[] {
  return routeeNames(thread.routed_to).map((who) => ({ thread, who, since: thread.routed_at }));
}

/* ---------------------------------------------------------------- */
/* Why not (Routed away)                                             */
/* ---------------------------------------------------------------- */

/**
 * Why a routed-away item cannot be nudged right now, in Jess's words, or
 * null when it can. The same rule the backend enforces (kanban 86ff18c8:
 * a nudge never goes around the hand-off check): the hand-off Merlin is
 * still checking or held as already known, or one that never reached them.
 */
export function routedNudgeBlock(thread: Pick<DoorThreadRead, 'route_notified' | 'routed_to'>): string | null {
  const them = thread.routed_to || 'they';
  switch (thread.route_notified) {
    case 'pending':
      return `Merlin is still checking whether ${them} already know this; a nudge waits for that.`;
    case 'held':
      return `Merlin held this hand-off as already known. Open the item to send it anyway first.`;
    case 'not_sent':
    case 'failed':
      return `${them} never received this item. Open it and send the hand-off first.`;
    default:
      return null;
  }
}

/* ---------------------------------------------------------------- */
/* Words                                                             */
/* ---------------------------------------------------------------- */

/** "Nudge Laura Caswell? Merlin sends them a new message saying you asked
 *  on Oct 6 and are still waiting." */
export function nudgeConfirmText(target: Pick<NudgeTarget, 'who' | 'since'>): string {
  const day = shortDate(target.since);
  const asked = day ? `you asked on ${day}` : 'you asked';
  return `Nudge ${target.who}? Merlin sends them a new message saying ${asked} and are still waiting.`;
}

/** "Nudge Laura Caswell about all 3 items? …", one message per item. */
export function nudgePersonConfirmText(who: string, count: number): string {
  const items = count === 1 ? '1 item' : `all ${count} items`;
  return `Nudge ${who} about ${items}? Merlin sends them one message per item saying you asked and are still waiting.`;
}

/** "Nudged Laura Caswell · Oct 21" for a row; '' when never nudged. */
export function nudgedLine(thread: Pick<DoorThreadRead, 'last_nudge'>): string {
  const n = thread.last_nudge;
  if (!n) return '';
  const day = shortDate(n.at);
  return `Nudged ${n.who}${day ? ` · ${day}` : ''}`;
}

/** "Nudged Laura Caswell about 3 items" / "Nudged … about 2 of 3 items; 1
 *  could not be: <why>". */
export function nudgePersonSummary(who: string, sent: number, refused: string[]): string {
  const total = sent + refused.length;
  if (refused.length === 0) return `Nudged ${who} about ${sent === 1 ? '1 item' : `${sent} items`}.`;
  const could = refused.length === 1 ? '1 could not be' : `${refused.length} could not be`;
  return `Nudged ${who} about ${sent} of ${total} items; ${could}: ${refused.join(' ')}`;
}
